import { useState, useEffect, useRef } from 'react';
import { WhatsappLogo, QrCode, CheckCircle, Warning, PaperPlaneRight, ArrowClockwise, SignOut, ChatCircleDots } from '@phosphor-icons/react';
import api from '../api';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const LBL = { display: 'block', ...MONO, fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 };
const INP = { width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)', padding: '8px 10px',
  color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11, outline: 'none', boxSizing: 'border-box' };

const NOTIFICATION_EVENTS = [
  { key: 'order_created',       label: 'New Sales Order Created',     channel: 'Sales Manager' },
  { key: 'qc_hold',             label: 'QC Hold Alert',               channel: 'Quality Manager' },
  { key: 'dispatch_confirmed',  label: 'Dispatch Confirmed',          channel: 'Dispatch + Customer' },
  { key: 'invoice_created',     label: 'Invoice Generated',           channel: 'Finance Team' },
  { key: 'capa_raised',         label: 'CAPA Raised',                 channel: 'Quality Manager' },
  { key: 'po_approved',         label: 'Purchase Order Approved',     channel: 'Procurement' },
];

function StatusBadge({ status }) {
  const map = {
    connected:    { color: 'hsl(142,70%,45%)', bg: 'hsl(142,30%,8%)',  label: 'Connected',    border: 'hsl(142,50%,20%)' },
    qr_ready:     { color: 'hsl(43,96%,56%)',  bg: 'hsl(43,70%,8%)',   label: 'Scan QR Code', border: 'hsl(43,60%,20%)' },
    connecting:   { color: 'hsl(197,100%,48%)',bg: 'hsl(197,50%,8%)',  label: 'Connecting…',  border: 'hsl(197,60%,20%)' },
    disconnected: { color: 'hsl(0,0%,50%)',    bg: 'hsl(0,0%,8%)',     label: 'Disconnected', border: 'hsl(0,0%,18%)' },
  };
  const s = map[status] || map.disconnected;
  return (
    <span style={{ ...MONO, fontSize: 10, color: s.color, background: s.bg,
      border: `1px solid ${s.border}`, padding: '3px 10px', textTransform: 'uppercase',
      letterSpacing: '0.1em', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.color,
        animation: status === 'connecting' ? 'pulse 1.5s infinite' : 'none' }} />
      {s.label}
    </span>
  );
}

export default function WhatsApp() {
  const [status, setStatus]   = useState(null);
  const [qr, setQR]           = useState(null);
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [phone, setPhone]     = useState('');
  const [message, setMessage] = useState('');
  const [sendResult, setSendResult] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get('/whatsapp/status');
      setStatus(r.data);
      if (r.data.has_qr) {
        const qr = await api.get('/whatsapp/qr');
        setQR(qr.data.qr);
      } else {
        setQR(null);
      }
    } catch (_) {
      setStatus({ status: 'disconnected', last_error: 'Service unavailable' });
    }
    setLoading(false);
  };

  const fetchLogs = async () => {
    try {
      const r = await api.get('/whatsapp/logs');
      setLogs(r.data);
    } catch (_) {}
  };

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    pollRef.current = setInterval(() => { fetchStatus(); fetchLogs(); }, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!phone || !message) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await api.post('/whatsapp/send', { phone, message });
      setSendResult({ ok: true, msg: `Sent to ${r.data.jid}` });
      setMessage('');
      fetchLogs();
    } catch (err) {
      setSendResult({ ok: false, msg: err.response?.data?.detail || err.message });
    }
    setSending(false);
  };

  const handleLogout = async () => {
    if (!window.confirm('Disconnect WhatsApp? You will need to scan QR again.')) return;
    await api.post('/whatsapp/logout').catch(() => {});
    setQR(null);
    setStatus(s => ({ ...s, status: 'disconnected' }));
    setTimeout(fetchStatus, 2000);
  };

  const connStatus = status?.status || 'disconnected';
  const isConnected = connStatus === 'connected';

  return (
    <div data-testid="whatsapp-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ ...MONO, fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)',
            textTransform: 'uppercase', marginBottom: 4, fontFamily: "'Clash Display', sans-serif" }}>WhatsApp</h1>
          <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Baileys · Automated ERP Notifications
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {status && <StatusBadge status={connStatus} />}
          <button onClick={fetchStatus} data-testid="refresh-status-btn"
            className="btn-ghost" style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
            <ArrowClockwise size={13} /> Refresh
          </button>
          {isConnected && (
            <button onClick={handleLogout} data-testid="wa-logout-btn"
              className="btn-ghost" style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 11, color: 'hsl(0,84%,60%)' }}>
              <SignOut size={13} /> Disconnect
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', padding: 40, textAlign: 'center' }}>
          Connecting to WhatsApp service…
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 12 }}>

          {/* Left column — QR / Status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Connection panel */}
            <div style={{ background: 'hsl(0,0%,5%)', border: `1px solid ${isConnected ? 'hsl(142,50%,20%)' : 'hsl(0,0%,14%)'}`, padding: 20 }}>
              <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase',
                letterSpacing: '0.12em', marginBottom: 14 }}>Connection</div>

              {isConnected ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <CheckCircle size={48} color="hsl(142,70%,45%)" weight="fill" />
                  <div style={{ ...MONO, fontSize: 13, color: 'hsl(142,70%,55%)', marginTop: 12, fontWeight: 700 }}>
                    WhatsApp Connected
                  </div>
                  {status?.phone && (
                    <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,60%)', marginTop: 6 }}>
                      +{status.phone} · {status.name || 'Unknown'}
                    </div>
                  )}
                  <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,40%)', marginTop: 4 }}>
                    {status?.message_count || 0} messages sent this session
                  </div>
                </div>
              ) : qr ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...MONO, fontSize: 10, color: 'hsl(43,96%,56%)', marginBottom: 12 }}>
                    Open WhatsApp → Linked Devices → Link a Device
                  </div>
                  <img data-testid="wa-qr-image" src={qr} alt="WhatsApp QR Code"
                    style={{ width: 220, height: 220, border: '3px solid hsl(0,0%,20%)', borderRadius: 4 }} />
                  <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', marginTop: 8 }}>
                    QR refreshes automatically
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <WhatsappLogo size={48} color="hsl(0,0%,25%)" />
                  <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,50%)', marginTop: 12 }}>
                    {connStatus === 'connecting' ? 'Initializing WhatsApp…' : 'Waiting for QR code…'}
                  </div>
                  {status?.last_error && (
                    <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,84%,60%)', marginTop: 6 }}>
                      {status.last_error}
                    </div>
                  )}
                  <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,35%)', marginTop: 8 }}>
                    Auto-polling every 5 seconds
                  </div>
                </div>
              )}
            </div>

            {/* Notification triggers */}
            <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 18 }}>
              <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase',
                letterSpacing: '0.12em', marginBottom: 12 }}>Auto-Notification Triggers</div>
              {NOTIFICATION_EVENTS.map(ev => (
                <div key={ev.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '6px 0', borderBottom: '1px solid hsl(0,0%,9%)' }}>
                  <div>
                    <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,75%)' }}>{ev.label}</div>
                    <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', marginTop: 1 }}>→ {ev.channel}</div>
                  </div>
                  <span style={{ ...MONO, fontSize: 9, color: isConnected ? 'hsl(142,70%,55%)' : 'hsl(0,0%,35%)',
                    background: isConnected ? 'hsl(142,30%,8%)' : 'hsl(0,0%,8%)', padding: '2px 8px',
                    border: `1px solid ${isConnected ? 'hsl(142,50%,20%)' : 'hsl(0,0%,15%)'}` }}>
                    {isConnected ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — Test + Log */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Send test message */}
            <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 20 }}>
              <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase',
                letterSpacing: '0.12em', marginBottom: 14 }}>Send Test Message</div>
              <form onSubmit={handleSend} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={LBL}>Phone Number (with country code)</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. 919876543210" data-testid="wa-phone-input"
                    style={INP} required />
                  <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,35%)', marginTop: 4 }}>
                    Format: 91XXXXXXXXXX (no + or spaces)
                  </div>
                </div>
                <div>
                  <label style={LBL}>Message</label>
                  <input value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Test message from ARIA Forge ERP" data-testid="wa-message-input"
                    style={INP} required />
                </div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button type="submit" data-testid="wa-send-btn" disabled={!isConnected || sending}
                    className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                    <PaperPlaneRight size={13} />
                    {sending ? 'Sending…' : 'Send Message'}
                  </button>
                  {!isConnected && (
                    <span style={{ ...MONO, fontSize: 10, color: 'hsl(0,84%,60%)' }}>
                      Connect WhatsApp first
                    </span>
                  )}
                  {sendResult && (
                    <span style={{ ...MONO, fontSize: 10, color: sendResult.ok ? 'hsl(142,70%,55%)' : 'hsl(0,84%,60%)' }}>
                      {sendResult.ok ? '✓' : '✗'} {sendResult.msg}
                    </span>
                  )}
                </div>
              </form>
            </div>

            {/* Message log */}
            <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 18, flex: 1, minHeight: 200 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase',
                  letterSpacing: '0.12em' }}>Message Log ({logs.length})</div>
                <ChatCircleDots size={14} color="hsl(0,0%,35%)" />
              </div>
              {logs.length === 0 ? (
                <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,30%)', textAlign: 'center', padding: '24px 0' }}>
                  No messages sent yet
                </div>
              ) : logs.map((log, i) => (
                <div key={i} data-testid={`wa-log-${i}`}
                  style={{ padding: '8px 0', borderBottom: '1px solid hsl(0,0%,9%)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ ...MONO, fontSize: 10, color: 'hsl(197,100%,48%)' }}>
                      {log.phone?.replace('@s.whatsapp.net','') || log.phone}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ ...MONO, fontSize: 9, color: log.status === 'sent' ? 'hsl(142,70%,55%)' : 'hsl(0,84%,60%)' }}>
                        {log.status?.toUpperCase()}
                      </span>
                      <span style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,35%)' }}>
                        {new Date(log.sent_at).toLocaleTimeString('en-IN')}
                      </span>
                    </div>
                  </div>
                  <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,60%)', marginTop: 2 }}>
                    {log.message?.length > 80 ? log.message.slice(0,80)+'…' : log.message}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
